import dayjs from 'dayjs';
import { useState } from 'react';
import Filter from './Filter';
import { ReactComponent as Icon } from './assets/icon.svg';
import { Button, message, Tabs, Tooltip } from 'antd';
import styled from '@emotion/styled';
import { useRequest } from 'ahooks';
import { getNegativeOverview, getTimeRange } from './service';
import { LoadingOutlined } from '@ant-design/icons';
import { useTranslate } from '@tolgee/react';
import { formatNumber } from 'utils/numberFormat';
import NegativeReason from './NegativeReason';
import useModal from 'hooks/useModal';
import ReviewModal, { ReviewModalProps } from './ReviewModal';
import NegativeItem from './NegativeItem';
import { What } from 'views/Aide/Scenario/PolicyTable';

function Negative({
  visible,
  departments,
}: {
  visible: boolean;
  departments: Dashboard.Department[];
}) {
  const { t } = useTranslate();

  const reviewModalState = useModal<Omit<ReviewModalProps, 'onOpenConversation'>>();

  const reviewModal = reviewModalState.meta && (
    <ReviewModal
      key={reviewModalState.meta.reviewIds.join(',')}
      {...reviewModalState.meta}
      visible={reviewModalState.visible}
      onCancel={() => reviewModalState.hide()}
    />
  );

  const [filterValue, setFilterValue] = useState<ReviewAnalysis.OverviewFilter>(() => ({
    platform: undefined,
    storeInfo: {},
    startTime: dayjs().subtract(7, 'days').startOf('days').valueOf().toString(),
    endTime: dayjs().subtract(1, 'days').endOf('days').valueOf().toString(),
    stars: [],
    spuKeys: [],
    itemRange: 'ALL',
  }));

  const timeRangeState = useRequest(
    () => {
      return getTimeRange()
        .catch(() => {
          return {
            timeRange: 'LAST_30_DAY',
          };
        })
        .then(({ timeRange }) => {
          if (timeRange === 'LAST_30_DAY') {
            setFilterValue({
              ...filterValue,
              startTime: dayjs().subtract(30, 'days').startOf('days').valueOf().toString(),
            });
          }
          return timeRange;
        });
    },
    {
      onError: () => message.error(t('phrase.获取数据失败，请稍后再试')),
    },
  );

  const { loading, data } = useRequest(() => getNegativeOverview(filterValue), {
    ready: Boolean(timeRangeState.data),
    refreshDeps: [filterValue],
    onError: () => message.error(t('phrase.获取数据失败，请稍后再试')),
  });

  const style = visible ? undefined : { display: 'none' };

  if (!timeRangeState.data) {
    return <div style={style}>{timeRangeState.loading && <LoadingOutlined />}</div>;
  }

  const filter = <Filter departments={departments} value={filterValue} onChange={setFilterValue} />;

  if (!data) {
    return (
      <div style={style}>
        {filter}
        {loading && <LoadingOutlined />}
      </div>
    );
  }

  const {
    negativeReviewCount,
    negativeReviewPercent,
    itemCount,
    problemCount,
    unCategorizedCount,
    unCategorizedReviewIds,
  } = data;

  const formatter = (num: number) => formatNumber(num, '0,0');

  const overview = (
    <Overview>
      <dl style={{ background: 'linear-gradient(#E8EFFF, #F5F8FF)' }}>
        <dt>
          {t('差评数量')}
          <Tooltip title={t('差评数量tips')}>
            <What />
          </Tooltip>
        </dt>
        <dd>{formatter(negativeReviewCount)}</dd>
        <Icon style={{ color: '#36F' }} />
      </dl>
      <dl style={{ background: 'linear-gradient(#FFF4E8, #FFFAF4)' }}>
        <dt>
          {t('差评率')}
          <Tooltip title={t('差评率tips')}>
            <What />
          </Tooltip>
        </dt>
        <dd>{formatNumber(Number(negativeReviewPercent || 0) / 100, '0.00%')}</dd>
        <Icon style={{ color: '#F59433' }} />
      </dl>
      <dl style={{ background: 'linear-gradient(#E1F4FF, #F5FCFF)' }}>
        <dt>
          {t('涉及商品数')}
          <Tooltip title={t('涉及商品数tips')}>
            <What />
          </Tooltip>
        </dt>
        <dd>{formatter(itemCount)}</dd>
        <Icon style={{ color: '#2DAEF8' }} />
      </dl>
      <dl style={{ background: 'linear-gradient(#FFE8EE, #FFF5FB)' }}>
        <dt>
          {t('发现问题数')}
          <Tooltip title={t('发现问题数tips')}>
            <What />
          </Tooltip>
        </dt>
        <dd>{formatter(problemCount)}</dd>
        <Icon style={{ color: '#F85D82' }} />
      </dl>
    </Overview>
  );

  const tabs = (
    <Tabs
      tabBarExtraContent={
        <Button
          type="text"
          onClick={() =>
            reviewModalState.show({
              reviewIds: unCategorizedReviewIds,
              count: unCategorizedCount,
              uncategorized: true,
            })
          }
        >
          {t('查看未分类评价')}({unCategorizedCount})
        </Button>
      }
    >
      <Tabs.TabPane key="差评归因" tab={t('差评归因')}>
        <NegativeReason filter={filterValue} />
      </Tabs.TabPane>
      <Tabs.TabPane key="商品分布" tab={t('商品分布')}>
        <NegativeItem filter={filterValue} />
      </Tabs.TabPane>
    </Tabs>
  );

  return (
    <div style={style}>
      {filter}
      {overview}
      {tabs}
      {reviewModal}
    </div>
  );
}

const Overview = styled.ul`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: #fff;
  > dl {
    flex: 1 0 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    position: relative;
    height: 90px;
    padding: 0 24px;
    margin-bottom: 0;
    border-radius: 8px;
    overflow: hidden;
    > dt {
      margin-bottom: 4px;
      font-weight: 400;
      color: #84868c;
    }
    > dd {
      margin-bottom: 0;
      font-size: 20px;
      font-weight: 500;
      color: #515359;
    }
    > svg {
      position: absolute;
      right: 0;
      bottom: 0;
      width: 48px;
    }
  }
`;

export default Negative;
